import { ClientUser, ClientUserRole, ClientUserRoleHierarchy } from '@zerops/models/client-user/client-user.model';
import {
  CLIENT_USER_ROLE_PRESETS,
  ClientUserRolePreset,
  RoleFlagValues,
  formatRoleCode
} from '@zerops/models/client-user/client-user.constant';
import { Project, ProjectRole } from '@zerops/models/project';

/**
 * Role tiers, mirroring the `Allowed roles:` annotations in the OpenAPI spec — every
 * endpoint allows a contiguous top slice of OWNER > ADMIN > BASIC_USER > READ_ONLY >
 * NO_ACCESS, and these are the only slices the API uses.
 *
 * Billing and project creation are deliberately absent: the API allows all five roles
 * there and gates on the flags instead. Use the flag helpers below, never a role check.
 */
export const ownerOnly = (): ClientUserRole[] => [
  ClientUserRole.Owner
];

export const adminAndAbove = (): ClientUserRole[] => [
  ClientUserRole.Owner,
  ClientUserRole.Admin
];

export const basicUserAndAbove = (): ClientUserRole[] => [
  ClientUserRole.Owner,
  ClientUserRole.Admin,
  ClientUserRole.BasicUser
];

export const readOnlyAndAbove = (): ClientUserRole[] => [
  ClientUserRole.Owner,
  ClientUserRole.Admin,
  ClientUserRole.BasicUser,
  ClientUserRole.ReadOnly
];

export function roleIs(role: string, ...allowed: Array<ClientUserRole | ProjectRole>): boolean {
  return !!role && (allowed as string[]).includes(role);
}

// Unknown roles rank below every known one, so an unrecognised value can never win a
// comparison.
export function roleRank(role: string): number {
  const index = ClientUserRoleHierarchy.indexOf(role);
  return index === -1 ? -1 : ClientUserRoleHierarchy.length - index;
}

export function hasLowerPermissionsThan(role: string, thanRole: string): boolean {
  return roleRank(role) < roleRank(thanRole);
}

// # Permission flags
//
// The flags are authoritative — the API allows all five roles on every endpoint that
// requires one. The role fallbacks only guard against a partial payload, since the
// backend guarantees the flag cannot be off for those roles.

export function canViewFinances(clientUser: ClientUser): boolean {
  if (!clientUser) {
    return false;
  }

  return !!clientUser.canViewFinances
    || !!clientUser.canEditFinances
    || roleIs(clientUser.roleCode, ...ownerOnly());
}

export function canEditFinances(clientUser: ClientUser): boolean {
  if (!clientUser) {
    return false;
  }

  return !!clientUser.canEditFinances || roleIs(clientUser.roleCode, ...ownerOnly());
}

export function canCreateProjects(clientUser: ClientUser): boolean {
  if (!clientUser) {
    return false;
  }

  return !!clientUser.canCreateProjects || roleIs(clientUser.roleCode, ...adminAndAbove());
}

// Thin wrappers kept while call sites migrate to roleIs(...) + tiers.

export function hasBasicUserRoleOrHigher(role: string) {
  return roleIs(role, ...basicUserAndAbove());
}

export function hasAdminRoleOrHigher(role: string) {
  return roleIs(role, ...adminAndAbove());
}

// A project override supersedes the client-level role even when it *lowers* access.
export function getEffectiveProjectRole(project: Project, clientUser: ClientUser): string | null {
  if (!project || !clientUser) {
    return null;
  }

  const override = project.userRoles?.find((u) => u.clientUserId === clientUser.id);

  return override ? override.roleCode : (clientUser.roleCode || null);
}

// Write access: services, deploys, envs, terminal, VPN keys, backups.
export function hasProjectFullAccess(project: Project, clientUser: ClientUser): boolean {
  return roleIs(getEffectiveProjectRole(project, clientUser), ...basicUserAndAbove());
}

export function hasProjectReadAccess(project: Project, clientUser: ClientUser): boolean {
  return roleIs(getEffectiveProjectRole(project, clientUser), ...readOnlyAndAbove());
}

// Delete, settings, mode upgrade, IPv4 request — a BASIC_USER can operate a project
// but not destroy or reconfigure it.
export function hasProjectAdminAccess(project: Project, clientUser: ClientUser): boolean {
  return roleIs(getEffectiveProjectRole(project, clientUser), ...adminAndAbove());
}

export type ProjectRoleVariant = 'full' | 'basic' | 'read' | 'none';

export interface ProjectRoleDisplay {
  label: string;
  icon: string;
  // Carried here so a chip's colour cannot disagree with its label.
  variant: ProjectRoleVariant;
}

/**
 * All five roles can be stored as a project override, and the API hands OWNER to
 * whoever created the project — a two-way Full/Read split mislabels an owner as read
 * only. Unknown values fall back to `None` rather than overstating access.
 */
export function getProjectRoleDisplay(roleCode: string): ProjectRoleDisplay {
  if (roleIs(roleCode, ...adminAndAbove())) {
    return { label: 'Full', icon: 'lock_open', variant: 'full' };
  }

  if (roleIs(roleCode, ClientUserRole.BasicUser)) {
    return { label: 'Basic', icon: 'settings', variant: 'basic' };
  }

  if (roleIs(roleCode, ClientUserRole.ReadOnly)) {
    return { label: 'Read', icon: 'visibility', variant: 'read' };
  }

  return { label: 'None', icon: 'block', variant: 'none' };
}

export interface RoleEditorLock {
  disabled: boolean;
  // Set when the API guarantees a value regardless of input. The form must hold it,
  // not merely render it, or a preset click leaves an illegal combination in the payload.
  forcedValue?: boolean;
  reason?: string;
}

export interface RoleEditorLocks {
  canCreateProjects: RoleEditorLock;
  canViewFinances: RoleEditorLock;
  canEditFinances: RoleEditorLock;
  assignableRoles: Record<string, RoleEditorLock>;
}

const UNLOCKED: RoleEditorLock = { disabled: false };

// Mirrors the API's own rules: only an Owner may set the finance flags, flags implied
// by a role cannot be turned off for it, and an Admin may assign only BASIC_USER and below.
export function getRoleEditorLocks(
  editorRoleCode: string,
  target: RoleFlagValues
): RoleEditorLocks {
  const editorIsOwner = roleIs(editorRoleCode, ClientUserRole.Owner);
  const targetIsOwner = roleIs(target?.roleCode, ClientUserRole.Owner);
  const targetIsAdmin = roleIs(target?.roleCode, ClientUserRole.Admin);

  const ownerForced: RoleEditorLock = {
    disabled: true, forcedValue: true, reason: 'Always enabled for Owner'
  };
  const notOwnerEditor: RoleEditorLock = {
    disabled: true, reason: 'Only an Owner can change this'
  };

  let canCreateProjects: RoleEditorLock = UNLOCKED;
  if (targetIsOwner) {
    canCreateProjects = ownerForced;
  } else if (targetIsAdmin) {
    canCreateProjects = { disabled: true, forcedValue: true, reason: 'Always enabled for Admin' };
  }

  let canEditFinances: RoleEditorLock = UNLOCKED;
  if (targetIsOwner) {
    canEditFinances = ownerForced;
  } else if (!editorIsOwner) {
    canEditFinances = notOwnerEditor;
  }

  let canViewFinances: RoleEditorLock = UNLOCKED;
  if (targetIsOwner) {
    canViewFinances = ownerForced;
  } else if (!editorIsOwner) {
    canViewFinances = notOwnerEditor;
  } else if (target?.canEditFinances) {
    canViewFinances = { disabled: true, forcedValue: true, reason: 'Implied by Can edit finances' };
  }

  return {
    canCreateProjects,
    canViewFinances,
    canEditFinances,
    assignableRoles: getAssignableRoleLocks(editorRoleCode)
  };
}

function getAssignableRoleLocks(editorRoleCode: string): Record<string, RoleEditorLock> {
  const all = [
    ClientUserRole.Owner,
    ClientUserRole.Admin,
    ClientUserRole.BasicUser,
    ClientUserRole.ReadOnly,
    ClientUserRole.NoAccess
  ];

  return all.reduce((acc, role) => {
    if (roleIs(editorRoleCode, ClientUserRole.Owner)) {
      acc[role] = UNLOCKED;
    } else if (roleIs(editorRoleCode, ClientUserRole.Admin)) {
      acc[role] = roleIs(role, ...adminAndAbove())
        ? { disabled: true, reason: 'Admins cannot assign this role' }
        : UNLOCKED;
    } else {
      acc[role] = { disabled: true, reason: 'You cannot assign roles' };
    }
    return acc;
  }, {} as Record<string, RoleEditorLock>);
}

export const ROLE_FLAG_KEYS = [
  'canCreateProjects',
  'canViewFinances',
  'canEditFinances'
] as const;

export type RoleFlagKey = typeof ROLE_FLAG_KEYS[number];

export const ROLE_FLAG_LABELS: Record<RoleFlagKey, string> = {
  canCreateProjects: 'Can create projects',
  canViewFinances: 'Can view finances',
  canEditFinances: 'Can edit finances'
};

export interface RoleBadgeDisplay {
  label: string;
  extraFlags: RoleFlagKey[];
}

// The flags a role turns on by itself. Asking as an Owner isolates what the role
// enforces from what the editor happens to be allowed to change.
function roleImpliedFlags(roleCode: string): RoleFlags {
  const locks = getRoleEditorLocks(ClientUserRole.Owner, { roleCode });

  return {
    canCreateProjects: locks.canCreateProjects.forcedValue === true,
    canViewFinances: locks.canViewFinances.forcedValue === true,
    canEditFinances: locks.canEditFinances.forcedValue === true
  };
}

const presetFlagCount = (preset: ClientUserRolePreset) =>
  ROLE_FLAG_KEYS.filter((flag) => preset[flag]).length;

// A preset whose flags the value already holds lends its name, so Developer survives
// a finance flag being added. Whatever the label leaves out comes back as extraFlags.
export function getRoleBadgeDisplay(value: RoleFlagValues): RoleBadgeDisplay {
  if (!value?.roleCode) {
    return { label: '', extraFlags: [] };
  }

  const preset = CLIENT_USER_ROLE_PRESETS
    .filter((p) => p.roleCode === value.roleCode
      && ROLE_FLAG_KEYS.every((flag) => !p[flag] || !!value[flag]))
    .sort((a, b) => presetFlagCount(b) - presetFlagCount(a))[0];

  const implied = preset ?? roleImpliedFlags(value.roleCode);

  return {
    label: preset ? preset.label : formatRoleCode(value.roleCode),
    extraFlags: ROLE_FLAG_KEYS.filter((flag) => !!value[flag] && !implied[flag])
  };
}

export interface RoleFlags {
  canCreateProjects: boolean;
  canViewFinances: boolean;
  canEditFinances: boolean;
}

/**
 * Flags are rebased on the new role rather than carried over, because a value may be
 * set only because the *previous* role forced it — Owner forces all three on, so
 * Owner -> Admin would otherwise silently leave finance access enabled.
 *
 * Flags the editor may not change keep their current value: resetting them would show
 * `false` while the API preserves what is stored, the exact display/reality gap this
 * editor exists to close.
 */
export function rebaseFlagsOnRole(
  editorRoleCode: string,
  roleCode: string,
  current: RoleFlagValues
): RoleFlags {
  // false so the baseline is what the role implies on its own, before any implication
  // from a flag that is itself being rebased.
  const locks = getRoleEditorLocks(editorRoleCode, { roleCode, canEditFinances: false });

  const resolve = (flag: keyof RoleFlags): boolean => {
    const lock = locks[flag];

    if (lock.forcedValue !== undefined) {
      return lock.forcedValue;
    }

    return lock.disabled ? !!current?.[flag] : false;
  };

  return {
    canCreateProjects: resolve('canCreateProjects'),
    canViewFinances: resolve('canViewFinances'),
    canEditFinances: resolve('canEditFinances')
  };
}
